import { cloneDeep, get as getProperty } from 'lodash';
import { create } from 'zustand';
import { getBehavior, getFramework } from '@/framework';
import { type ILayerProps, type IPoint } from '@/version-types';
import { getAdapter } from '../adapter';
import { EDIT_AREA_ID_SELECTOR, TEXT_EDIT_EVENTS } from '../constants';
import {
    checkHasTextNode,
    ensureEditAreaHasDefaultStruct,
    RangeHelper,
    setCaretPosition,
    setCaretToEnd,
} from '../utils';
import { type State, type Store } from './types';

const initialState: State = {
    editingRange: null,
    disableSelectEvent: false,
    originLayer: null,
    editingLayer: null,
};

export const createStore = (identity: string) => {
    return create<Store>((set, get) => ({
        ...initialState,

        setEditingRange: (range) => {
            set({ editingRange: range });
        },

        getEditingRange: () => get().editingRange,

        preventDefaultSelectEvent: () => {
            set({ disableSelectEvent: true });
        },

        allowDefaultSelectEvent: () => {
            set({ disableSelectEvent: false });
        },

        setOriginLayer: (layer: ILayerProps | null) => {
            set({ originLayer: layer });
        },

        getOriginLayer: () => get().originLayer,

        setEditingLayer: (layer: ILayerProps | null) => {
            set({ editingLayer: layer });
        },

        getEditingLayer: () => get().editingLayer,

        enterEditing: async (layerID: string, point?: IPoint) => {
            const framework = getFramework(identity);
            let layer = framework.getLayer(layerID, true);

            if (!layer) return;

            const editingLayer = get().editingLayer;
            if (editingLayer) await get().exitEditing();

            const layerType = layer.layerType;
            const adapter = getAdapter(layerType);
            if (!adapter) return;

            layer = adapter.onBeforeEnterEditing(framework, layer);

            framework.events.emit(TEXT_EDIT_EVENTS.BEFORE_ENTER_EDITING, layer);

            set({ originLayer: cloneDeep(layer), editingLayer: layer, disableSelectEvent: false });

            layer = adapter.onAfterEnterEditing(framework, layer);

            // 加载字体
            // TODO: 提取到外部 util 中
            const behavior = getBehavior(identity, layer);
            if (behavior && behavior.getTextInfo) {
                const textInfo = behavior.getTextInfo(layer);
                const fontFamilies = textInfo.map(({ fontFamily }) => fontFamily);
                const tasks = fontFamilies.map(async (fontFamily) => {
                    return framework.registerFont(layer, fontFamily, false);
                });
                Promise.all(tasks).then(() => framework.operate.setActiveLayer(layerID));
            }

            setTimeout(() => {
                const $editArea = document.getElementById(EDIT_AREA_ID_SELECTOR) as HTMLDivElement;
                if (!$editArea) return;

                $editArea.focus();

                const hasTextNode = checkHasTextNode($editArea);
                if (!hasTextNode) {
                    return ensureEditAreaHasDefaultStruct($editArea);
                }

                const hasModified = getProperty(layer, 'extraInfo.modified', false);
                if (!hasModified) {
                    RangeHelper.selectAll($editArea);
                } else {
                    if (point) {
                        setCaretPosition($editArea, point);
                    } else {
                        if (hasTextNode) setCaretToEnd($editArea);
                    }
                }
            }, 0);
        },

        exitEditing: async () => {
            const editingLayer = get().editingLayer;
            if (!editingLayer) return;

            const framework = getFramework(identity);
            const originLayer = get().originLayer;

            if (!originLayer) return;

            set({ originLayer: null, editingLayer: null, editingRange: null });

            const latestLayer = framework.getLayer(editingLayer.id, true);
            if (!latestLayer) return; // 有可能在编辑态时直接删除图层

            const adapter = getAdapter(latestLayer.layerType);
            if (adapter) {
                adapter.onExitEditing(framework, originLayer, latestLayer);
            }
        },

        reset: () => {
            set(initialState);
        },
    }));
};
